import { Field } from "@sitecore-jss/sitecore-jss-react";
import React, { ChangeEvent } from "react";

import { ModelState } from "../../interfaces/modelState";
import { Tooltip } from "../tooltip/Tooltip";
import { ValidationMessage } from "../validationMessage/ValidationMessage";
import styles from "./Checkboxes.module.scss";
import useFieldData from "./useFieldData";
import useFieldName from "./useFieldName";

export interface CheckboxOption<T> {
  label: string;
  value: T;
}

interface Props<T> {
  fieldData: Record<string, Field<string>>;
  name: string;
  fieldName?: string;
  modelState?: ModelState | null;
  setModelState?: (modelState: ModelState) => void;
  inputValue: T[];
  id?: string;
  onChange?: (value: T[]) => void;
  propertyName: string;
  options: CheckboxOption<T>[];
  orientation?: "horizontal" | "vertical";
}

export const Checkboxes = <T extends string>({ orientation = "horizontal", ...props }: Props<T>) => {
  const { name, fieldName } = useFieldName(props.name, props.fieldName);
  const { heading, tooltip } = useFieldData(props.fieldData, name);

  let selectedValues = [...props.inputValue];

  const onFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
    const fieldValue = e.target.value as T;
    if (selectedValues.includes(fieldValue)) {
      selectedValues = selectedValues.filter((item) => {
        return item !== fieldValue;
      });
    } else {
      selectedValues.push(fieldValue);
    }

    if (props.onChange) {
      props.onChange(selectedValues);
    }
  };

  return (
    <div className="form-row">
      <div className="form-item">
        <div className="form-input">
          <div className="form-label">
            {heading.value}
            <Tooltip fieldName={fieldName} value={tooltip.value} />
          </div>
        </div>
        <div
          className={`${styles.checkboxes__wrapper} ${
            orientation === "vertical" ? styles.checkboxes__wrapperVertical : ""
          }`}
        >
          {props.options.map(({ value, label }, i) => {
            const cbFieldName = `${fieldName}${i}${value}`;
            const tooltipFieldName = `Tooltip${cbFieldName.replace(/\d+/g, "")}`;
            const cbTooltip = props.fieldData[tooltipFieldName]?.value;

            return (
              <React.Fragment key={i}>
                <input
                  className="form-input-checkbox"
                  id={cbFieldName}
                  name={fieldName}
                  type="checkbox"
                  value={value}
                  checked={selectedValues.includes(value)}
                  onChange={onFieldChange}
                />
                <label htmlFor={cbFieldName}>
                  {label}
                  <Tooltip fieldName={cbFieldName} value={cbTooltip} />
                </label>
              </React.Fragment>
            );
          })}
        </div>
      </div>
      <ValidationMessage modelState={props.modelState} property={props.propertyName} />
    </div>
  );
};
